/**
 * Cookie Consent Management Utilities
 * Handles GDPR/CCPA-compliant cookie consent storage and retrieval
 */

export type CookieCategory = 'essential' | 'functional' | 'analytics';

export interface ConsentPreferences {
  essential: boolean; // Always true, strictly necessary
  functional: boolean; // Sidebar, theme, etc. - treated as strictly necessary
  analytics: boolean; // PostHog tracking
  timestamp: number;
}

const CONSENT_KEY = 'cookie-consent';
const CONSENT_EXPIRY_MONTHS = 12;
const BANNER_DISMISSED_KEY = 'cookie-banner-dismissed';

/**
 * Check if user's browser has Do Not Track enabled
 */
export function isDNTEnabled(): boolean {
  return (
    navigator.doNotTrack === '1' ||
    // @ts-ignore - Some browsers use different properties
    window.doNotTrack === '1' ||
    // @ts-ignore
    navigator.msDoNotTrack === '1'
  );
}

/**
 * Get current consent preferences from localStorage
 * Returns null if no consent has been given yet
 */
export function getConsentPreferences(): ConsentPreferences | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;

    const preferences: ConsentPreferences = JSON.parse(stored);
    
    // Check if consent has expired (12 months)
    const expiryDate = new Date(preferences.timestamp);
    expiryDate.setMonth(expiryDate.getMonth() + CONSENT_EXPIRY_MONTHS);
    
    if (new Date() > expiryDate) {
      // Consent expired, clear it
      clearConsent();
      return null;
    }

    return preferences;
  } catch (error) {
    console.error('Error reading consent preferences:', error);
    return null;
  }
}

/**
 * Save consent preferences to localStorage
 */
export function saveConsentPreferences(preferences: Omit<ConsentPreferences, 'timestamp'>): void {
  try {
    const consentData: ConsentPreferences = {
      ...preferences,
      timestamp: Date.now(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(BANNER_DISMISSED_KEY, 'true');
  } catch (error) {
    console.error('Error saving consent preferences:', error);
  }
}

/**
 * Check if analytics cookies are allowed
 * Respects DNT header and user consent preferences
 */
export function getAnalyticsConsent(): boolean {
  // Respect Do Not Track
  if (isDNTEnabled()) {
    return false;
  }

  const preferences = getConsentPreferences();
  
  // If no preferences set, default to false (no tracking until consent)
  if (!preferences) {
    return false;
  }

  return preferences.analytics;
}

/**
 * Check if the cookie banner should be shown
 * Returns true if user hasn't made a choice yet
 */
export function shouldShowBanner(): boolean {
  // Don't show if user has already dismissed or made a choice
  const dismissed = localStorage.getItem(BANNER_DISMISSED_KEY);
  const preferences = getConsentPreferences();
  
  return !dismissed && !preferences;
}

/**
 * Accept all cookies
 */
export function acceptAllCookies(): void {
  saveConsentPreferences({
    essential: true,
    functional: true,
    analytics: true,
  });
}

/**
 * Reject non-essential cookies (only allow essential)
 */
export function rejectNonEssentialCookies(): void {
  saveConsentPreferences({
    essential: true,
    functional: true, // Treated as strictly necessary
    analytics: false,
  });
  
  // Clear any existing PostHog cookies
  clearPostHogCookies();
}

/**
 * Clear all consent preferences
 */
export function clearConsent(): void {
  try {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(BANNER_DISMISSED_KEY);
  } catch (error) {
    console.error('Error clearing consent:', error);
  }
}

/**
 * Clear PostHog cookies from browser
 */
export function clearPostHogCookies(): void {
  // Clear PostHog localStorage items
  const keys = Object.keys(localStorage);
  keys.forEach(key => {
    if (key.startsWith('ph_')) {
      localStorage.removeItem(key);
    }
  });

  // Clear PostHog cookies
  document.cookie.split(';').forEach(cookie => {
    const [name] = cookie.split('=');
    const trimmedName = name.trim();
    if (trimmedName.startsWith('ph_')) {
      document.cookie = `${trimmedName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
}

/**
 * Update specific consent preference
 */
export function updateConsentPreference(category: CookieCategory, value: boolean): void {
  const currentPreferences = getConsentPreferences() || {
    essential: true,
    functional: true,
    analytics: false,
    timestamp: Date.now(),
  };

  // Essential and functional cannot be disabled (strictly necessary)
  if (category === 'essential' || category === 'functional') {
    return;
  }

  saveConsentPreferences({
    ...currentPreferences,
    [category]: value,
  });

  // If analytics is being disabled, clear PostHog cookies
  if (category === 'analytics' && !value) {
    clearPostHogCookies();
  }
}
